import { Canvas } from "@craftjs/core";
import { Gift } from "lucide-react";

import { define, prop } from "@/components/builder/utils";

import { ButtonBlock, ConditionBlock, ConditionCaseBlock, BlockHelperBlock, HbsVariableBlock, HeadingBlock, InputBlock, RecaptchaBlock, TagBlock, TextBlock, HTMLBlock, MultiConditionBlock } from "..";
import { useModuleMetadata } from "../../utils/tailwindConfig/hooks";
import BasePreview from "../base";

import { CONTAINER_OFFERS_PROPERTIES, CONTENT_PROPERTIES, DESCRIPTION_PROPERTIES, HEADER_OFFERS_PROPERTIES, MODAL_PROPERTIES, OFFERS_HTML, POINTS_PROPERTIES, TAG_PROPERTIES, TRIGGER_PROPPERTIES, HEADER_PROPERTIES, SCOREBOARD_PROPERTIES, FORM_PROPERTIES, HEADER_IMAGE_PROPS, completedProperties } from "./utils";

function RewardPreview({ ...props }) {
  useModuleMetadata('session', true)
  useModuleMetadata('rewards', true)

  return <BasePreview {...props}>
    <div>
      <Canvas
        is={ConditionBlock}
        id="reward"
        properties={{ condition: 'showReward', show: 'reward-status' }}
        custom={{ state: { label: 'Page' } }}>
        <Canvas
          is={ConditionCaseBlock}
          custom={{ state: { type: 'reward-status', name: 'Status' } }}>
          <Canvas is={TagBlock} properties={{ tagName: 'div', layoutType: 'flex', flex: { direction: 'flex-col', align: 'items-center', distribute: 'justify-center' }, border: { radius: { rounded: 'lg' }, width: { border: '1' }, color: 'gray-200' }, shadow: { value: 'md' }, space: { padding: 'p-6' } }} custom={{ name: 'reward-status-header' }}>
            <Canvas
              is={TagBlock}
              properties={{ tagName: 'div', layoutType: 'flex', flex: { direction: 'flex-col', align: 'items-center', distribute: 'justify-center' }, size: { width: 'w-full', height: { mobile: 'h-auto', desktop: 'h-24' } }, space: { padding: { mobile: 'py-4', desktop: null } } }}
              custom={{ name: 'reward-status-header-container' }}
            >
              <Canvas
                is={TextBlock}
                properties={{ content: 'Reward Status', fontSize: { mobile: 'xl', desktop: '3xl' }, fontWeight: 'bold', textColor: 'cyan-500', align: 'text-center', space: { marginBottom: 'mb-2' } }}
              />
              <Canvas
                is={TextBlock}
                properties={{ content: 'Track your progress and manage your active rewards', fontSize: { mobile: 'sm', desktop: 'base' }, textColor: 'gray-600', align: 'text-center' }}
              />
            </Canvas>
            <Canvas
              is={TagBlock}
              properties={HEADER_PROPERTIES}>
              <Canvas
                is={TagBlock}
                properties={{ tagName: 'div', layoutType: 'flex', flex: { direction: 'flex-row', gap: '4', align: 'items-center' }, size: { width: 'w-full' } }}
                custom={{ name: 'title-container' }}>
                <Canvas
                  is={ConditionBlock}
                  properties={{ condition: 'showSelect' }}>
                  <Canvas
                    is={ConditionCaseBlock}>
                    <Canvas
                      is={TagBlock}
                      properties={{ tagName: 'div', position: { value: 'relative' }, size: { width: { mobile: 'w-full', desktop: 'w-md' } } }}
                      custom={{ name: 'custom-dropdown' }}>
                      <Canvas
                        is={InputBlock}
                        properties={{ hide: { mobile: { default: null, "peer-checked": null } }, cursor: "cursor-pointer", opacity: { checked: 0, default: null }, attributes: { type: "checkbox", id: 'dropdown-toggle-rewards', className: "hidden peer" }, nativeStyle: "appearance-none", peerPropagation: true }}
                      />
                      <Canvas
                        is={TagBlock}
                        properties={{ tagName: 'label', attributes: { for: 'dropdown-toggle-rewards' }, layoutType: 'flex', flex: { direction: 'flex-row', distribute: 'justify-between', align: 'items-center' }, size: { width: 'w-full' }, background: { color: 'white' }, border: { radius: { rounded: 'lg' }, color: 'gray-300', width: { border: '1' } }, space: { padding: { mobile: 'p-3', desktop: 'p-4' } }, cursor: 'cursor-pointer', shadow: { value: 'sm' } }}
                        custom={{ name: 'custom-dropdown-trigger' }}>
                        <Canvas
                          is={TagBlock}
                          properties={{ tagName: 'div', layoutType: 'flex', flex: { direction: 'flex-row', gap: { mobile: '2', desktop: '3' }, align: 'items-center' } }}
                          custom={{ name: 'custom-dropdown-selected' }}>
                          <Canvas
                            is={ConditionBlock}
                            properties={{ condition: '(truthy reward.id)', show: 'has-reward' }}>
                            <Canvas
                              is={ConditionCaseBlock}>
                              <Canvas
                                is={TagBlock}
                                properties={{ tagName: 'div', layoutType: 'flex', flex: { direction: 'flex-col', gap: { mobile: '0', desktop: '1' } } }}
                                custom={{ name: 'dropdown-selected-info' }}>
                                <Canvas
                                  is={TextBlock}
                                  properties={{ content: '{{reward_name}}', fontSize: { mobile: 'base', desktop: '2xl' }, fontWeight: 'semibold', textColor: 'gray-900' }} />
                                <Canvas
                                  is={TextBlock}
                                  properties={{ content: 'Signed up: {{reward_subscribed_at}}', fontSize: { mobile: '8px', desktop: 'xs' }, textColor: 'gray-500' }} />
                              </Canvas>
                            </Canvas>
                            <Canvas
                              is={ConditionCaseBlock}>
                            </Canvas>
                          </Canvas>
                        </Canvas>
                        <Canvas
                          is={HbsVariableBlock}
                          content=''
                          html='<svg class="w-4 h-4 md:w-5 md:h-5 text-gray-400" width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
                          custom={{ name: 'dropdown-icon' }} />
                      </Canvas>
                      <Canvas
                        is={TagBlock}
                        properties={{ tagName: 'div', attributes: { id: 'rewardDropdownMenu' }, position: { value: 'absolute' }, size: { width: 'w-full' }, background: { color: 'white' }, border: { radius: { rounded: 'lg' }, color: 'gray-300', width: { border: '1' } }, space: { marginTop: 'mt-2', padding: { mobile: 'p-1.5', desktop: 'p-2' } }, shadow: { value: 'lg' }, customClassName: ['hidden', 'peer-checked:block', 'z-50', 'max-h-96', 'overflow-y-auto'], customClassNameOptions: [{ label: 'hidden', value: 'hidden' }, { label: 'peer-checked:block', value: 'peer-checked:block' }, { label: 'z-50', value: 'z-50' }, { label: 'max-h-96', value: 'max-h-96' }, { label: 'overflow-y-auto', value: 'overflow-y-auto' }] }}
                        custom={{ name: 'custom-dropdown-menu' }}>
                        <Canvas
                          is={ConditionBlock}
                          properties={{ condition: '(truthy reward_completions.length)' }}>
                          <Canvas
                            is={ConditionCaseBlock}>
                            <Canvas
                              is={ConditionBlock}
                              properties={{ condition: 'show_points_separator' }}>
                              <Canvas
                                is={ConditionCaseBlock}>
                                <Canvas
                                  is={TagBlock}
                                  properties={{ tagName: 'div', space: { marginBottom: 'mb-2' } }}
                                  custom={{ name: 'points-section' }}>
                                  <Canvas
                                    is={TextBlock}
                                    properties={{ content: 'Points', fontSize: { mobile: 'xs', desktop: 'sm' }, fontWeight: 'semibold', textColor: 'gray-700', space: { padding: { mobile: 'px-2 py-1.5', desktop: 'px-3 py-2' } }, background: { color: 'gray-100' }, border: { width: { borderBottom: '1' }, color: 'gray-200' } }} />
                                  <Canvas
                                    is={BlockHelperBlock}
                                    helper="each"
                                    parameters={[['reward_completions']]}
                                    custom={{ name: 'points-rewards' }}>
                                    <Canvas
                                      is={ConditionBlock}
                                      properties={{ condition: '(eq reward_type "points")' }}>
                                      <Canvas
                                        is={ConditionCaseBlock}>
                                        <Canvas
                                          is={TagBlock}
                                          properties={{ tagName: 'form', attributes: { method: 'POST' } }}
                                          custom={{ name: 'dropdown-option-form' }}>
                                          <Canvas
                                            is={TagBlock}
                                            properties={{ tagName: 'input', attributes: { type: 'hidden', name: 'reward_completion_id', value: '{{reward_completion_id}}' } }}
                                            custom={{ name: 'reward-completion-input', suppressChildren: true }} />
                                          <Canvas
                                            is={TagBlock}
                                            properties={{ tagName: 'button', attributes: { type: 'submit' }, layoutType: 'flex', flex: { direction: 'flex-row', distribute: 'justify-between', align: 'items-start', gap: { mobile: '2', desktop: '3' } }, size: { width: 'w-full' }, space: { padding: { mobile: 'px-2 py-1.5', desktop: 'px-3 py-2' } }, border: { radius: { rounded: 'md' } }, cursor: 'cursor-pointer', textAlign: { horizontal: 'left' }, customClassName: ['hover:bg-gray-50'], customClassNameOptions: [{ label: 'hover:bg-gray-50', value: 'hover:bg-gray-50' }] }}
                                            custom={{ name: 'dropdown-option-button' }}>
                                            <Canvas
                                              is={TagBlock}
                                              properties={{ tagName: 'div', layoutType: 'flex', flex: { direction: 'flex-col', gap: '1', align: 'items-start' }, customClassName: ['flex-1'], customClassNameOptions: [{ label: 'flex-1', value: 'flex-1' }] }}
                                              custom={{ name: 'dropdown-option-content' }}>
                                              <Canvas
                                                is={TextBlock}
                                                properties={{ content: '{{reward_name}}', fontSize: { mobile: 'xs', desktop: 'sm' }, fontWeight: 'medium', textColor: 'gray-900' }} />
                                              <Canvas
                                                is={TextBlock}
                                                properties={{ content: 'Signed up: {{reward_subscribed_at}}', fontSize: { mobile: '8px', desktop: 'xs' }, textColor: 'gray-500' }} />
                                            </Canvas>
                                            <Canvas
                                              is={ConditionBlock}
                                              properties={{ condition: `(or (eq reward_completion_status 'active') (eq reward_completion_status 'completed') (eq reward_completion_status 'paid'))`, show: 'active' }}
                                              custom={{ state: { label: 'Points options status' } }}
                                            >
                                              <Canvas
                                                is={ConditionCaseBlock}
                                                custom={{ state: { name: 'Active', type: 'active' } }}
                                              >
                                                <Canvas
                                                  is={TextBlock}
                                                  properties={{ content: '{{reward_completion_status}}', fontSize: { mobile: 'xs', desktop: 'sm' }, textColor: 'blue-700', background: { color: 'blue-100' }, space: { padding: { mobile: 'px-2 py-1', desktop: 'px-3 py-1.5' } }, border: { radius: { rounded: 'full' }, color: 'blue-200', width: { border: '1' } }, customClassName: ['w-min', 'whitespace-nowrap', 'capitalize'], customClassNameOptions: [{ label: 'w-min', value: 'w-min' }, { label: 'whitespace-nowrap', value: 'whitespace-nowrap' }, { label: 'capitalize', value: 'capitalize' }] }} />
                                              </Canvas>
                                              <Canvas
                                                is={ConditionCaseBlock}
                                                custom={{ state: { name: 'Disabled', type: 'disabled' } }}
                                              >
                                                <Canvas
                                                  is={TextBlock}
                                                  properties={{ content: '{{reward_completion_status}}', fontSize: { mobile: 'xs', desktop: 'sm' }, textColor: 'gray-700', background: { color: 'gray-100' }, space: { padding: { mobile: 'px-2 py-1', desktop: 'px-3 py-1.5' } }, border: { radius: { rounded: 'full' }, color: 'gray-200', width: { border: '1' } }, customClassName: ['w-min', 'whitespace-nowrap', 'capitalize'], customClassNameOptions: [{ label: 'w-min', value: 'w-min' }, { label: 'whitespace-nowrap', value: 'whitespace-nowrap' }, { label: 'capitalize', value: 'capitalize' }] }} />
                                              </Canvas>
                                            </Canvas>
                                          </Canvas>
                                        </Canvas>
                                      </Canvas>
                                    </Canvas>
                                  </Canvas>
                                </Canvas>
                              </Canvas>
                            </Canvas>
                            <Canvas
                              is={ConditionBlock}
                              properties={{ condition: 'show_deals_separator' }}>
                              <Canvas
                                is={ConditionCaseBlock}>
                                <Canvas
                                  is={TagBlock}
                                  properties={{ tagName: 'div', space: { marginBottom: 'mb-2' } }}
                                  custom={{ name: 'deals-section' }}>
                                  <Canvas
                                    is={TextBlock}
                                    properties={{ content: 'Deals', fontSize: { mobile: 'xs', desktop: 'sm' }, fontWeight: 'semibold', textColor: 'gray-700', space: { padding: { mobile: 'px-2 py-1.5', desktop: 'px-3 py-2' } }, background: { color: 'gray-100' }, border: { width: { borderBottom: '1' }, color: 'gray-200' } }} />
                                  <Canvas
                                    is={BlockHelperBlock}
                                    helper="each"
                                    parameters={[['reward_completions']]}
                                    custom={{ name: 'deals-rewards' }}>
                                    <Canvas
                                      is={ConditionBlock}
                                      properties={{ condition: '(eq reward_type "deals_only")' }}>
                                      <Canvas
                                        is={ConditionCaseBlock}>
                                        <Canvas
                                          is={TagBlock}
                                          properties={{ tagName: 'form', attributes: { method: 'POST' } }}
                                          custom={{ name: 'dropdown-option-form' }}>
                                          <Canvas
                                            is={TagBlock}
                                            properties={{ tagName: 'input', attributes: { type: 'hidden', name: 'reward_completion_id', value: '{{reward_completion_id}}' } }}
                                            custom={{ name: 'reward-completion-input', suppressChildren: true }} />
                                          <Canvas
                                            is={TagBlock}
                                            properties={{ tagName: 'button', attributes: { type: 'submit' }, layoutType: 'flex', flex: { direction: 'flex-row', distribute: 'justify-between', align: 'items-start', gap: { mobile: '2', desktop: '3' } }, size: { width: 'w-full' }, space: { padding: { mobile: 'px-2 py-1.5', desktop: 'px-3 py-2' } }, border: { radius: { rounded: 'md' } }, cursor: 'cursor-pointer', textAlign: { horizontal: 'left' }, customClassName: ['hover:bg-gray-50'], customClassNameOptions: [{ label: 'hover:bg-gray-50', value: 'hover:bg-gray-50' }] }}
                                            custom={{ name: 'dropdown-option-button' }}>
                                            <Canvas
                                              is={TagBlock}
                                              properties={{ tagName: 'div', layoutType: 'flex', flex: { direction: 'flex-col', gap: '1', align: 'items-start' }, customClassName: ['flex-1'], customClassNameOptions: [{ label: 'flex-1', value: 'flex-1' }] }}
                                              custom={{ name: 'dropdown-option-content' }}>
                                              <Canvas
                                                is={TextBlock}
                                                properties={{ content: '{{reward_name}}', fontSize: { mobile: 'xs', desktop: 'sm' }, fontWeight: 'medium', textColor: 'gray-900' }} />
                                              <Canvas
                                                is={TextBlock}
                                                properties={{ content: 'Signed up: {{reward_subscribed_at}}', fontSize: { mobile: '8px', desktop: 'xs' }, textColor: 'gray-500' }} />
                                            </Canvas>
                                            <Canvas
                                              is={ConditionBlock}
                                              properties={{ condition: `(or (eq reward_completion_status 'active') (eq reward_completion_status 'completed') (eq reward_completion_status 'paid'))`, show: 'active' }}
                                              custom={{ state: { label: 'Deals options status' } }}
                                            >
                                              <Canvas
                                                is={ConditionCaseBlock}
                                                custom={{ state: { name: 'Active', type: 'active' } }}
                                              >
                                                <Canvas
                                                  is={TextBlock}
                                                  properties={{ content: '{{reward_completion_status}}', fontSize: { mobile: 'xs', desktop: 'sm' }, textColor: 'blue-700', background: { color: 'blue-100' }, space: { padding: { mobile: 'px-2 py-1', desktop: 'px-3 py-1.5' } }, border: { radius: { rounded: 'full' }, color: 'blue-200', width: { border: '1' } }, customClassName: ['w-min', 'whitespace-nowrap', 'capitalize'], customClassNameOptions: [{ label: 'w-min', value: 'w-min' }, { label: 'whitespace-nowrap', value: 'whitespace-nowrap' }, { label: 'capitalize', value: 'capitalize' }] }} />
                                              </Canvas>
                                              <Canvas
                                                is={ConditionCaseBlock}
                                                custom={{ state: { name: 'Disabled', type: 'disabled' } }}
                                              >
                                                <Canvas
                                                  is={TextBlock}
                                                  properties={{ content: '{{reward_completion_status}}', fontSize: { mobile: 'xs', desktop: 'sm' }, textColor: 'gray-700', background: { color: 'gray-100' }, space: { padding: { mobile: 'px-2 py-1', desktop: 'px-3 py-1.5' } }, border: { radius: { rounded: 'full' }, color: 'gray-200', width: { border: '1' } }, customClassName: ['w-min', 'whitespace-nowrap', 'capitalize'], customClassNameOptions: [{ label: 'w-min', value: 'w-min' }, { label: 'whitespace-nowrap', value: 'whitespace-nowrap' }, { label: 'capitalize', value: 'capitalize' }] }} />
                                              </Canvas>
                                            </Canvas>
                                          </Canvas>
                                        </Canvas>
                                      </Canvas>
                                    </Canvas>
                                  </Canvas>
                                </Canvas>
                              </Canvas>
                            </Canvas>
                          </Canvas>
                        </Canvas>
                        <Canvas
                          is={ConditionBlock}
                          properties={{ condition: 'show_ignite_separator' }}>
                          <Canvas
                            is={ConditionCaseBlock}>
                            <Canvas
                              is={TagBlock}
                              properties={{ tagName: 'div', space: { marginBottom: 'mb-2' } }}
                              custom={{ name: 'ignite-section' }}>
                              <Canvas
                                is={TextBlock}
                                properties={{ content: 'Ignite Your Rewards', fontSize: { mobile: 'xs', desktop: 'sm' }, fontWeight: 'semibold', textColor: 'gray-700', space: { padding: { mobile: 'px-2 py-1.5', desktop: 'px-3 py-2' } }, background: { color: 'gray-100' }, border: { width: { borderBottom: '1' }, color: 'gray-200' } }} />
                              <Canvas
                                is={BlockHelperBlock}
                                helper="each"
                                parameters={[['master_rewards']]}
                                custom={{ name: 'master-rewards' }}>
                                <Canvas
                                  is={TagBlock}
                                  properties={{ tagName: 'form', attributes: { method: 'POST' } }}
                                  custom={{ name: 'dropdown-option-form' }}>
                                  <Canvas
                                    is={TagBlock}
                                    properties={{ tagName: 'input', attributes: { type: 'hidden', name: '{{#if reward_completion_id}}reward_completion_id{{else}}master_reward_id{{/if}}', value: '{{#if reward_completion_id}}{{reward_completion_id}}{{else}}{{master_reward_id}}{{/if}}' } }}
                                    custom={{ name: 'master-reward-input', suppressChildren: true }} />
                                  <Canvas
                                    is={TagBlock}
                                    properties={{ tagName: 'button', attributes: { type: 'submit' }, layoutType: 'flex', flex: { direction: 'flex-row', distribute: 'justify-between', align: 'items-start', gap: '3' }, size: { width: 'w-full' }, space: { padding: 'px-3 py-2' }, border: { radius: { rounded: 'md' } }, cursor: 'cursor-pointer', textAlign: { horizontal: 'left' }, customClassName: ['hover:bg-gray-50'], customClassNameOptions: [{ label: 'hover:bg-gray-50', value: 'hover:bg-gray-50' }] }}
                                    custom={{ name: 'dropdown-option-button' }}>
                                    <Canvas
                                      is={TagBlock}
                                      properties={{ tagName: 'div', layoutType: 'flex', flex: { direction: 'flex-col', gap: '1', align: 'items-start' }, customClassName: ['flex-1'], customClassNameOptions: [{ label: 'flex-1', value: 'flex-1' }] }}
                                      custom={{ name: 'dropdown-option-content' }}>
                                      <Canvas
                                        is={TextBlock}
                                        properties={{ content: '{{reward_name}}', fontSize: { mobile: 'xs', desktop: 'sm' }, fontWeight: 'medium', textColor: 'gray-900' }} />
                                      <Canvas
                                        is={TextBlock}
                                        properties={{ content: 'Signed up: {{reward_subscribed_at}}', fontSize: { mobile: '8px', desktop: 'xs' }, textColor: 'gray-500' }} />
                                    </Canvas>
                                    <Canvas
                                      is={ConditionBlock}
                                      properties={{ condition: `(or (eq reward_completion_status 'active') (eq reward_completion_status 'completed') (eq reward_completion_status 'paid'))`, show: 'active' }}
                                      custom={{ state: { label: 'Ignite options status' } }}
                                    >
                                      <Canvas
                                        is={ConditionCaseBlock}
                                        custom={{ state: { name: 'Active', type: 'active' } }}
                                      >
                                        <Canvas
                                          is={TextBlock}
                                          properties={{ content: '{{reward_completion_status}}', fontSize: { mobile: 'xs', desktop: 'sm' }, textColor: 'blue-700', background: { color: 'blue-100' }, space: { padding: { mobile: 'px-2 py-1', desktop: 'px-3 py-1.5' } }, border: { radius: { rounded: 'full' }, color: 'blue-200', width: { border: '1' } }, customClassName: ['w-min', 'whitespace-nowrap', 'capitalize'], customClassNameOptions: [{ label: 'w-min', value: 'w-min' }, { label: 'whitespace-nowrap', value: 'whitespace-nowrap' }, { label: 'capitalize', value: 'capitalize' }] }} />
                                      </Canvas>
                                      <Canvas
                                        is={ConditionCaseBlock}
                                        custom={{ state: { name: 'Disabled', type: 'disabled' } }}
                                      >
                                        <Canvas
                                          is={TextBlock}
                                          properties={{ content: '{{reward_completion_status}}', fontSize: { mobile: 'xs', desktop: 'sm' }, textColor: 'gray-700', background: { color: 'gray-100' }, space: { padding: { mobile: 'px-2 py-1', desktop: 'px-3 py-1.5' } }, border: { radius: { rounded: 'full' }, color: 'gray-200', width: { border: '1' } }, customClassName: ['w-min', 'whitespace-nowrap', 'capitalize'], customClassNameOptions: [{ label: 'w-min', value: 'w-min' }, { label: 'whitespace-nowrap', value: 'whitespace-nowrap' }, { label: 'capitalize', value: 'capitalize' }] }} />
                                      </Canvas>
                                    </Canvas>
                                  </Canvas>
                                </Canvas>
                              </Canvas>
                            </Canvas>
                          </Canvas>
                        </Canvas>
                      </Canvas>
                    </Canvas>
                  </Canvas>
                </Canvas>
                <Canvas is={ConditionBlock} properties={{ show: 'true', condition: `(truthy reward_status)` }} >
                  <Canvas is={ConditionCaseBlock}>
                    <Canvas
                      is={ConditionBlock}
                      properties={{ show: 'active', condition: `(and reward.active (or (eq reward_status 'active') (eq reward_status 'completed') (eq reward_status 'paid')))` }}
                      custom={{ state: { label: 'Status Badge' } }}>
                      <Canvas
                        is={ConditionCaseBlock}
                        properties={{}}
                        custom={{ state: { name: 'Active', type: 'active' } }}>
                        <Canvas
                          is={TextBlock}
                          properties={{ content: '{{reward_status}}', fontSize: { mobile: 'xs', desktop: 'sm' }, textColor: 'blue-700', background: { color: 'blue-100' }, space: { padding: { mobile: 'px-2 py-1', desktop: 'px-3 py-1.5' } }, border: { radius: { rounded: 'full' }, color: 'blue-200', width: { border: '1' } }, customClassName: ['w-min', 'whitespace-nowrap'], customClassNameOptions: [{ label: 'w-min', value: 'w-min' }, { label: 'whitespace-nowrap', value: 'whitespace-nowrap' }] }} />
                      </Canvas>
                      <Canvas
                        is={ConditionCaseBlock}
                        custom={{ state: { type: 'disabled', name: 'Disabled' } }}>
                        <Canvas
                          is={TextBlock}
                          properties={{ content: '{{reward_status}}', fontSize: { mobile: 'xs', desktop: 'sm' }, textColor: 'gray-600', background: { color: 'gray-100' }, space: { padding: { mobile: 'px-2 py-1', desktop: 'px-3 py-1.5' } }, border: { radius: { rounded: 'full' }, color: 'gray-300', width: { border: '1' } }, customClassName: ['w-min', 'whitespace-nowrap'], customClassNameOptions: [{ label: 'w-min', value: 'w-min' }, { label: 'whitespace-nowrap', value: 'whitespace-nowrap' }] }} />
                      </Canvas>
                    </Canvas>
                  </Canvas>
                  <Canvas is={ConditionCaseBlock}></Canvas>
                </Canvas>
              </Canvas>
              <Canvas
                is={TagBlock}
                {...HEADER_IMAGE_PROPS} />
            </Canvas>
            <Canvas
              is={TagBlock}
              properties={{ tagName: 'div', layoutType: 'flex', flex: { direction: 'flex-row', gap: '2', align: 'items-start' }, space: { padding: 'p-6', margin: 'my-2' }, border: { radius: { rounded: 'lg' } }, background: { color: 'gray-50' }, customClassName: ['w-full'], customClassNameOptions: [{ label: 'w-full', value: 'w-full' }] }}>
              <Canvas
                is={MultiConditionBlock}
                properties={{ show: 'points' }}
                custom={{ state: { label: 'Reward Type Info' } }}>
                <Canvas
                  is={ConditionCaseBlock}
                  properties={{ condition: '(eq reward_type "points")' }}
                  custom={{ state: { name: 'Points', type: 'points' } }}>
                  <Canvas
                    is={TagBlock}
                    properties={{ tagName: 'div', layoutType: 'flex', flex: { direction: 'flex-row', gap: '2', align: 'items-center' } }}
                    custom={{ name: 'ignite-points-row' }}>
                    <Canvas
                      is={TextBlock}
                      properties={{ content: 'Points', fontSize: 'sm', fontWeight: 'semibold', textColor: 'gray-700', background: { color: 'gray-200' }, space: { padding: 'p-2' }, border: { radius: { rounded: 'md' }, color: 'gray-400', width: { border: '1' } }, customClassName: ['w-min', 'whitespace-nowrap'], customClassNameOptions: [{ label: 'w-min', value: 'w-min' }, { label: 'whitespace-nowrap', value: 'whitespace-nowrap' }] }} />
                    <Canvas
                      is={TextBlock}
                      properties={{ content: 'Points rewards require completing certain aspects of the site and a specific number of deals to accumulate points. Once you reach the required total points, you can redeem your reward.', fontSize: 'sm', textColor: 'gray-600' }} />
                  </Canvas>
                </Canvas>
                <Canvas
                  is={ConditionCaseBlock}
                  properties={{ condition: '(eq reward_type "deals_only")' }}
                  custom={{ state: { name: 'Deals', type: 'deals_only' } }}>
                  <Canvas
                    is={TagBlock}
                    properties={{ tagName: 'div', layoutType: 'flex', flex: { direction: 'flex-row', gap: '2', align: 'items-center' } }}
                    custom={{ name: 'ignite-deals-row' }}>
                    <Canvas
                      is={TextBlock}
                      properties={{ content: 'Deals', fontSize: 'sm', fontWeight: 'semibold', textColor: 'gray-700', background: { color: 'gray-200' }, space: { padding: 'p-2' }, border: { radius: { rounded: 'md' }, color: 'gray-400', width: { border: '1' } }, customClassName: ['w-min', 'whitespace-nowrap'], customClassNameOptions: [{ label: 'w-min', value: 'w-min' }, { label: 'whitespace-nowrap', value: 'whitespace-nowrap' }] }} />
                    <Canvas
                      is={TextBlock}
                      properties={{ content: 'Deals rewards require completing a specific number of deals. Once you reach the required number of deals, you can redeem your reward.', fontSize: 'sm', textColor: 'gray-600' }} />
                  </Canvas>
                </Canvas>
                <Canvas
                  is={ConditionCaseBlock}
                  properties={{ condition: '(eq reward_type "master_cashout")' }}
                  custom={{ state: { name: 'Ignite', type: 'master_cashout' } }}>
                  <Canvas
                    is={TagBlock}
                    properties={{ tagName: 'div', layoutType: 'flex', flex: { direction: 'flex-row', gap: '2', align: 'items-center' } }}
                    custom={{ name: 'ignite-master-cashout-row' }}>
                    <Canvas
                      is={TextBlock}
                      properties={{ content: 'Ignite Your Rewards', fontSize: 'sm', fontWeight: 'semibold', textColor: 'gray-700', background: { color: 'gray-200' }, space: { padding: 'p-2' }, border: { radius: { rounded: 'md' }, color: 'gray-400', width: { border: '1' } }, customClassName: ['w-min', 'whitespace-nowrap'], customClassNameOptions: [{ label: 'w-min', value: 'w-min' }, { label: 'whitespace-nowrap', value: 'whitespace-nowrap' }] }} />
                    <Canvas
                      is={TextBlock}
                      properties={{ content: 'Deals rewards require completing a specific number of deals. Once you reach the required number of deals, you can redeem your reward. If the reward is an "Ignite Your Rewards" you may combine your deals across all your reward signups. These deals will only count towards the same tier across all rewards. All rewards will be closed upon doing so except for the reward you choose to roll your deals into.', fontSize: 'sm', textColor: 'gray-600' }} />
                  </Canvas>
                </Canvas>
              </Canvas>
            </Canvas>
            <Canvas
              is={TagBlock}
              properties={{ tagName: 'div', layoutType: 'flex', flex: { direction: 'flex-col', gap: '4', distribute: 'justify-center', align: 'items-center' }, size: { width: 'w-full' } }}
              custom={{ name: 'ignite-container' }}>
              <Canvas
                is={ConditionBlock}
                properties={{ condition: 'showMasterForm' }}>
                <Canvas
                  is={ConditionCaseBlock}>
                  <Canvas
                    is={ConditionBlock}
                    properties={{ condition: 'showIgniteAction' }}>
                    <Canvas
                      is={TagBlock}
                      properties={{ tagName: 'div', layoutType: 'flex', flex: { direction: 'flex-row', gap: '4', align: 'items-center' }, space: { padding: 'p-6', margin: 'my-2' }, border: { radius: { rounded: 'lg' } }, background: { color: 'gray-50' }, customClassName: ['w-full'], customClassNameOptions: [{ label: 'w-full', value: 'w-full' }] }}
                      custom={{ name: 'ignite-toggle-container' }}>
                      <Canvas
                        is={ConditionCaseBlock}>
                        <Canvas
                          is={TextBlock}
                          properties={{ content: 'Ignite Your Rewards', fontSize: 'base', fontWeight: 'medium', textColor: 'gray-700', background: { color: 'gray-200' }, space: { padding: 'p-2' }, border: { radius: { rounded: 'md' }, color: 'gray-400', width: { border: '1' } }, customClassName: ['w-min', 'whitespace-nowrap'], customClassNameOptions: [{ label: 'w-min', value: 'w-min' }, { label: 'whitespace-nowrap', value: 'whitespace-nowrap' }] }} />
                        <Canvas
                          is={TagBlock}
                          properties={{ tagName: 'form', attributes: { id: 'ignite-form', method: 'POST' } }}
                          custom={{ name: 'ignite-form' }}>
                          <Canvas
                            is={TagBlock}
                            properties={{ tagName: 'input', attributes: { type: 'hidden', name: 'ignite_reward', value: 'true' } }}
                            custom={{ name: 'ignite-reward-input', suppressChildren: true }} />
                          <Canvas
                            is={HbsVariableBlock}
                            content=''
                            html='<input type="hidden" name="master_reward_id" value="{{reward_id}}" />'
                            custom={{ name: 'reward-completion-id-input' }} />
                          <Canvas
                            is={TagBlock}
                            properties={{ tagName: 'div', layoutType: 'flex', flex: { direction: 'flex-row', gap: '2', align: 'items-center' }, customClassName: ['cursor-pointer'], customClassNameOptions: [{ label: 'cursor-pointer', value: 'cursor-pointer' }], attributes: { id: 'ignite-toggle-wrapper' } }}
                            custom={{ name: 'ignite-toggle-wrapper' }}>
                            <Canvas
                              is={TagBlock}
                              properties={{ tagName: 'div', layoutType: 'flex', flex: { align: 'items-center' }, border: { radius: { rounded: 'full' }, width: { border: '2' }, color: 'gray-300' }, background: { color: 'gray-200' }, space: { padding: 'p-0.5' }, customClassName: ['w-11', 'h-6', 'relative'], customClassNameOptions: [{ label: 'w-11', value: 'w-11' }, { label: 'h-6', value: 'h-6' }, { label: 'relative', value: 'relative' }] }}
                              custom={{ name: 'ignite-toggle-track' }}>
                              <Canvas
                                is={TagBlock}
                                properties={{ tagName: 'div', border: { radius: { rounded: 'full' } }, background: { color: 'white' }, customClassName: ['w-4', 'h-4', 'shadow'], customClassNameOptions: [{ label: 'w-4', value: 'w-4' }, { label: 'h-4', value: 'h-4' }, { label: 'shadow', value: 'shadow' }] }}
                                custom={{ name: 'ignite-toggle-thumb', suppressChildren: true }} />
                            </Canvas>
                          </Canvas>
                        </Canvas>
                      </Canvas>
                    </Canvas>
                  </Canvas>
                </Canvas>
              </Canvas>
            </Canvas>
            <Canvas is={ConditionBlock} properties={{ show: 'true', condition: 'showMasterReward' }}>
              <Canvas is={ConditionCaseBlock}>
                <Canvas
                  is={TagBlock}
                  properties={{ tagName: 'div', layoutType: 'flex', flex: { direction: 'flex-row', gap: '4', align: 'items-center' }, space: { padding: 'p-6', margin: 'my-2' }, border: { radius: { rounded: 'lg' } }, background: { color: 'gray-50' }, size: { width: 'w-full' } }}
                  custom={{ name: 'ignite-toggle-container' }}>
                  <Canvas
                    is={TagBlock}
                    properties={{ tagName: 'form', attributes: { id: 'master-reward-form', method: 'POST' } }}>
                    <Canvas
                      is={HbsVariableBlock}
                      content=''
                      html='<input type="hidden" name="master_reward_id" value="{{master_reward_id}}" />'
                      custom={{ name: 'reward-completion-id-input' }} />
                    <Canvas
                      is={TagBlock}
                      properties={{
                        tagName: 'div',
                        layoutType: 'flex',
                        flex: { direction: 'flex-row', gap: '2', align: 'items-center' },
                      }}
                      custom={{ name: 'master-reward-text-container' }}>
                      <Canvas
                        is={TextBlock}
                        properties={{ content: 'This reward sends offers completed to ', fontSize: 'base', textColor: 'gray-700' }}
                      />
                      <Canvas
                        is={TagBlock}
                        properties={{ tagName: 'button', attributes: { type: 'submit' }, fontSize: 'base', fontWeight: 'medium', textColor: 'gray-700', cursor: 'cursor-pointer', background: { color: 'gray-100' }, border: { radius: { rounded: 'lg' }, color: 'gray-300', width: { border: '1' } }, space: { padding: 'px-3 py-2' }, customClassName: ['whitespace-nowrap'], customClassNameOptions: [{ label: 'whitespace-nowrap', value: 'whitespace-nowrap' }] }}
                      >
                        <Canvas
                          is={TextBlock}
                          properties={{ content: '{{master_reward_name}}' }}
                        />
                      </Canvas>
                    </Canvas>
                  </Canvas>
                </Canvas>
              </Canvas>
            </Canvas>
          </Canvas>
          <Canvas
            is={ConditionBlock}
            properties={{ show: 'false', condition: '(or (eq rewardCompletion.status "completed") (eq rewardCompletion.status "paid"))' }}
            custom={{ state: { label: 'Scoreboard' } }}
          >
            <Canvas
              is={ConditionCaseBlock}
              custom={{ state: { name: 'Completed', type: 'true' } }}
            >
              <Canvas is={TagBlock} properties={SCOREBOARD_PROPERTIES} custom={{ name: 'reward-scoreboard' }}>
                <Canvas is={TextBlock} properties={{ content: "Congratulations! You've completed this reward.", fontSize: '3xl', fontWeight: 'bold' }} />
              </Canvas>
            </Canvas>
            <Canvas is={ConditionCaseBlock} custom={{ state: { name: 'Not Completed', type: 'false' } }}>
              <Canvas
                is={TagBlock}
                properties={SCOREBOARD_PROPERTIES}
                custom={{ name: 'reward-scoreboard' }}>
                <Canvas
                  is={TextBlock}
                  properties={{ content: "Total Points Earned", fontSize: '3xl', fontWeight: 'bold' }} />
                <Canvas
                  is={TextBlock}
                  properties={{ content: "{{user_points}} of {{reward_points}}", fontSize: '3xl', fontWeight: 'bold' }} />
                <Canvas
                  is={TextBlock}
                  properties={{ content: "Keep completing offers to earn more rewards!", fontSize: 'lg' }} />
              </Canvas>
            </Canvas>
          </Canvas>

          <Canvas
            is={TagBlock}
            properties={{ tagName: 'div', layoutType: 'flex', flex: { direction: 'flex-col', gap: '4' } }}
            custom={{ name: 'steps-container' }}>
            <Canvas
              is={BlockHelperBlock}
              helper="each"
              parameters={[['reward_steps']]}
              custom={{ name: 'steps' }}>
              <Canvas
                is={ConditionBlock}
                properties={{ show: 'false', condition: '(eq reward_step_type "progression")' }}
                custom={{ state: { label: 'Step Type' } }}>
                <Canvas
                  is={ConditionCaseBlock}
                  properties={{}}
                  custom={{ state: { name: 'Progression', type: 'true' } }}>
                  <Canvas
                    is={ConditionBlock}
                    properties={{ show: 'true', condition: 'reward_step_completed' }}
                    custom={{ state: { label: 'Step Completed' } }}>
                    <Canvas
                      is={ConditionCaseBlock}
                      properties={{}}
                      custom={{ state: { name: 'Completed', type: 'true' } }}>
                      {renderProgressionStep('completed', completedProperties('green'))}
                    </Canvas>
                    <Canvas
                      is={ConditionCaseBlock}
                      properties={{}}
                      custom={{ state: { name: 'Not Completed', type: 'false' } }}>
                      {renderProgressionStep('pending', completedProperties('yellow'))}
                    </Canvas>
                  </Canvas>
                </Canvas>
                <Canvas
                  is={ConditionCaseBlock}
                  custom={{ state: { name: 'Offers', type: 'false' } }}
                >
                  <Canvas
                    is={TagBlock}
                    properties={CONTAINER_OFFERS_PROPERTIES}
                    custom={{ name: 'offers-container' }}>
                    <Canvas
                      is={TagBlock}
                      properties={HEADER_OFFERS_PROPERTIES}
                      custom={{ name: 'reward-offers-header' }}>
                      <Canvas
                        is={TextBlock}
                        properties={{ content: '{{reward_step_name}}', fontSize: '3xl', fontWeight: 'bold' }} />
                      <Canvas
                        is={TextBlock}
                        properties={{ content: '{{reward_step_description}}', ...DESCRIPTION_PROPERTIES }} />
                      <Canvas
                        is={MultiConditionBlock}
                        properties={{ show: 'modal' }}
                        custom={{ state: { label: 'Step Completed' } }}>
                        <Canvas
                          is={ConditionCaseBlock}
                          properties={{ condition: '(and (eq ../reward_type "points") reward_step_completed)' }}
                          custom={{ state: { name: 'Points', type: 'points' } }}>
                          <Canvas
                            is={TextBlock}
                            properties={{ content: '{{reward_step_points}} points', ...POINTS_PROPERTIES }} />
                        </Canvas>
                        <Canvas
                          is={ConditionCaseBlock}
                          properties={{ condition: '(and (ne ../reward_type "points") reward_step_completed)' }}
                          custom={{ state: { name: 'Completed', type: 'completed' } }}>
                          <Canvas
                            is={TextBlock}
                            properties={{ content: 'Step Completed!', fontSize: 'base', fontWeight: 'medium', textColor: 'green-600', background: { color: 'green-100' }, space: { padding: 'p-2' }, border: { radius: { rounded: 'md' }, color: 'green-600', width: { border: '1' } }, customClassName: ['w-min', 'whitespace-nowrap'], customClassNameOptions: [{ label: 'w-min', value: 'w-min' }, { label: 'whitespace-nowrap', value: 'whitespace-nowrap' }] }} />
                        </Canvas>
                        <Canvas
                          is={ConditionCaseBlock}
                          properties={{ condition: '(not reward_step_enable_modal)' }}
                          custom={{ state: { name: 'Disabled Modal', type: 'disabled-modal' } }}>
                          <Canvas
                            is={TextBlock}
                            properties={{ content: 'You need to complete the previous step to start this step.', fontSize: 'base', fontWeight: 'medium', textColor: 'gray-600', background: { color: 'gray-100' }, space: { padding: 'p-2' }, border: { radius: { rounded: 'md' }, color: 'gray-600', width: { border: '1' } } }} />
                        </Canvas>
                        <Canvas
                          is={ConditionCaseBlock}
                          properties={{ condition: 'reward_step_enable_modal' }}
                          custom={{ state: { name: 'Modal', type: 'modal' } }}>
                          <Canvas
                            is={InputBlock}
                            properties={{ ...TRIGGER_PROPPERTIES, attributes: { ...TRIGGER_PROPPERTIES.attributes, id: 'modal-toggle-{{reward_step_id}}' } }} />
                          <Canvas
                            is={TagBlock}
                            properties={MODAL_PROPERTIES}
                            custom={{ name: 'modal-overlay' }}>
                            <Canvas
                              is={TagBlock}
                              properties={{ tagName: 'label', position: { value: 'absolute' }, customClassName: ['inset-0', 'cursor-pointer'], customClassNameOptions: [{ label: 'inset-0', value: 'inset-0' }, { label: 'cursor-pointer', value: 'cursor-pointer' }], attributes: { for: 'modal-toggle-{{reward_step_id}}' } }}
                              custom={{ name: 'modal-backdrop', suppressChildren: true }} />
                            <Canvas
                              is={TagBlock}
                              properties={CONTENT_PROPERTIES}
                              custom={{ name: 'content' }}>
                              <Canvas
                                is={TagBlock}
                                properties={TAG_PROPERTIES}
                                custom={{ name: 'modal-content' }}>
                                <Canvas
                                  is={TagBlock}
                                  properties={{ tagName: 'div', layoutType: 'flex', flex: { direction: 'flex-row', distribute: 'justify-between', align: 'items-center' }, space: { marginBottom: 'mb-4' } }}
                                  custom={{ name: 'modal-header' }}>
                                  <Canvas
                                    is={TextBlock}
                                    properties={{ content: '{{reward_step_name}}', fontSize: '2xl', fontWeight: 'bold' }} />
                                  <Canvas
                                    is={TagBlock}
                                    properties={{ tagName: 'label', fontSize: '2xl', cursor: 'cursor-pointer', textColor: 'gray-500', attributes: { for: 'modal-toggle-{{reward_step_id}}' } }}
                                    custom={{ name: 'close-button' }}>
                                    <Canvas
                                      is={TextBlock}
                                      properties={{ content: '✕', fontSize: '2xl', textColor: 'gray-500' }} />
                                  </Canvas>
                                </Canvas>
                                <Canvas
                                  is={TagBlock}
                                  properties={{ tagName: 'div', size: { width: 'w-full' } }}
                                  attributes={{ id: `rewardOffersIframe-{{reward_step_id}}` }}
                                  custom={{ name: 'iframe-container' }}>
                                </Canvas>
                              </Canvas>
                            </Canvas>
                          </Canvas>
                        </Canvas>
                      </Canvas>
                    </Canvas>
                    <Canvas
                      is={TagBlock}
                      properties={{ tagName: 'div', background: { color: 'gray-200' }, size: { height: 'h-4', width: 'w-full' }, border: { radius: { rounded: '3xl' } } }}
                      custom={{ name: 'step-progress-bar' }}>
                      <Canvas
                        is={TagBlock}
                        attributes={{ id: 'reward-offers-step-progress-bar-value-{{reward_step_id}}' }}
                        properties={{ tagName: 'div', space: { padding: 'px-2' }, size: { width: 'w-[70%]', height: 'h-4' }, background: { color: 'gray-500' }, border: { radius: { rounded: '3xl' } }, textAlign: { horizontal: 'right', vertical: 'middle' }, textColor: 'gray-50' }}
                        custom={{ name: 'step-progress-bar-value' }}>
                        <Canvas
                          is={TextBlock}
                          attributes={{ id: 'reward-offers-step-progress-bar-value-text-{{reward_step_id}}' }}
                          properties={{ content: '{{reward_step_percentage}}%', fontSize: 'xs' }}
                          custom={{ name: 'progress-value-text' }} />
                      </Canvas>
                    </Canvas>
                    <Canvas
                      is={BlockHelperBlock}
                      helper="each"
                      parameters={[['reward_step_activities']]}
                      custom={{ name: 'each' }}>
                      <Canvas
                        is={TagBlock}
                        properties={{ tagName: 'div', layoutType: 'flex', flex: { direction: 'flex-col', gap: '3' } }}
                        custom={{ name: 'container' }}>
                        <Canvas
                          is={MultiConditionBlock}
                          properties={{ show: 'completed' }}
                          custom={{ state: { label: 'Offer Activities' } }}>
                          <Canvas
                            is={ConditionCaseBlock}
                            properties={{ condition: `(eq reward_step_activity_status "completed")` }}
                            custom={{ state: { name: 'Completed', type: 'completed' } }}>
                            {renderOfferActivity('completed')}
                          </Canvas>
                          <Canvas
                            is={ConditionCaseBlock}
                            properties={{ condition: `(eq reward_step_activity_status "pending")` }}
                            custom={{ state: { name: 'Pending', type: 'pending' } }}>
                            {renderOfferActivity('pending')}
                          </Canvas>
                          <Canvas
                            is={ConditionCaseBlock}
                            properties={{ condition: `(eq reward_step_activity_status "rejected")` }}
                            custom={{ state: { name: 'Rejected', type: 'rejected' } }}>
                            {renderOfferActivity('rejected')}
                          </Canvas>
                          <Canvas
                            is={ConditionCaseBlock}
                            properties={{ condition: `(eq reward_step_activity_status "ignited")` }}
                            custom={{ state: { name: 'Ignited', type: 'ignited' } }}>
                            {renderOfferActivity('ignited')}
                          </Canvas>
                        </Canvas>
                      </Canvas>
                    </Canvas>
                  </Canvas>
                  <Canvas
                    is={HTMLBlock}
                    properties={{ content: OFFERS_HTML }}
                    custom={{ name: 'offers-html' }}>
                  </Canvas>
                </Canvas>
              </Canvas>
            </Canvas>
          </Canvas>
        </Canvas>
        <Canvas
          is={ConditionCaseBlock}
          custom={{ state: { type: 'email-input', name: 'Email' } }}>
          <Canvas
            is={TagBlock}
            properties={{ tagName: 'div', layoutType: 'flex', flex: { direction: 'flex-col', gap: '4', distribute: 'justify-center', align: 'items-center' } }}
            custom={{ name: 'form-container' }} >
            <Canvas
              is={TagBlock}
              custom={{ name: 'form' }}
              properties={FORM_PROPERTIES.form}>
              <Canvas
                is={HeadingBlock}
                properties={{ tagName: 'h2', content: 'Enter your email' }} />
              <Canvas
                is={InputBlock}
                properties={FORM_PROPERTIES.input} />
              <Canvas
                is={RecaptchaBlock} />
              <Canvas
                is={ButtonBlock}
                attributes={{ type: 'submit' }}
                properties={FORM_PROPERTIES.button} />
              <Canvas
                is={ConditionBlock}
                properties={{ condition: 'rewardData.rewardNotFound' }}>
                <Canvas
                  is={ConditionCaseBlock}>
                  <Canvas
                    is={TagBlock}
                    custom={{ name: 'not-found-reward' }}
                    properties={{ tagName: 'div', layoutType: 'flex', flex: { distribute: 'justify-center', align: 'items-center' }, space: { padding: 'p-4' }, border: { radius: { rounded: 'md' }, color: 'yellow-300', width: { border: '1' } }, background: { color: 'yellow-50' } }}>
                    <Canvas
                      is={TextBlock}
                      custom={{ name: 'not-found-reward-text' }}
                      properties={{ content: 'Reward not found', textColor: 'yellow-300' }} />
                  </Canvas>
                </Canvas>
              </Canvas>
            </Canvas>
          </Canvas>
        </Canvas>
      </Canvas>
    </div>
  </BasePreview >
}

function renderProgressionStep(status, properties) {
  return <Canvas
    is={TagBlock}
    properties={{ ...properties, tagName: 'div' }}
    custom={{ name: 'progression-step-container' }}>
    <Canvas
      is={TextBlock}
      properties={{ content: '{{reward_step_name}}', fontSize: 'xl', fontWeight: 'bold' }} />
    <Canvas
      is={TagBlock}
      properties={{ tagName: 'div', layoutType: 'flex', flex: { direction: 'flex-col', gap: '2', align: 'items-center' } }}
      custom={{ name: 'reward-progression-status' }}>
      <Canvas
        is={TextBlock}
        properties={{ content: `${status === 'completed' ? '{{reward_step_points}}' : '0'}/{{reward_step_points}} points`, textColor: status === 'completed' ? 'green-500' : 'yellow-500' }} />
      <Canvas
        is={TextBlock}
        properties={{ content: status, textColor: status === 'completed' ? 'green-500' : 'yellow-500' }} />
    </Canvas>
  </Canvas>
}

function renderOfferActivity(status) {
  return <Canvas
    is={TagBlock}
    properties={{ tagName: 'div', layoutType: 'flex', flex: { direction: 'justify-between', align: 'items-center' }, space: { padding: 'p-4' }, border: { color: 'gray-200', width: { border: '2' }, radius: { rounded: 'md' } }, shadow: { value: 'lg' } }}
    custom={{ name: 'activity-container' }}>
    <Canvas
      is={TagBlock}
      properties={{ tagName: 'div', layoutType: 'flex', flex: { direction: 'flex-col', gap: '1' } }}
      custom={{ name: 'activity-info' }}>
      <Canvas
        is={TextBlock}
        properties={{ content: '{{reward_step_activity_name}}', fontSize: 'xl', fontWeight: 'semibold', textColor: 'gray-900' }} />
      <Canvas
        is={TextBlock}
        properties={{ content: '{{reward_step_activity_created_at}}', fontSize: 'xs', textColor: 'gray-500' }} />
    </Canvas>
    <Canvas
      is={TextBlock}
      properties={{ content: status, fontSize: 'sm', fontWeight: 'bold', textColor: "gray-50", background: { color: { completed: 'green-500', pending: 'yellow-500', rejected: 'red-500', ignited: 'red-500' }[status] }, space: { padding: 'p-2' }, border: { radius: { rounded: '2xl' } } }} />
  </Canvas>
}

function RewardHTML({ children, attributes }) {
  return <>
    {`{{#with rewardData}}`}
    <div {...attributes}>
      {children}
    </div>
    <script type="text/javascript" dangerouslySetInnerHTML={{
      __html: `
        var igniteToggle = document.getElementById('ignite-toggle-wrapper');
        if (igniteToggle) {
          igniteToggle.addEventListener('click', function() {
            document.getElementById('ignite-form').submit()
          })
        }

        var modalToggles = document.querySelectorAll('[id^="modal-toggle-"]');
        modalToggles.forEach(function(toggle) {
          toggle.addEventListener('change', function() {
            if (this.checked) {
              document.body.style.overflow = 'hidden';
              document.documentElement.style.overflow = 'hidden';
            } else {
              document.body.style.overflow = '';
              document.documentElement.style.overflow = '';
            }
          });
        });
      `,
    }} />
    {`{{/with}}`}
  </>
}

export default define('Reward', RewardPreview, RewardHTML)
  .palete(<Gift size={20} />, 'Reward', 'Embedded')
  .property(...prop.predefined.layout())
  .property(...prop.predefined.appearance())
  .defaultProps({
    layoutType: 'flex',
    flex: { direction: 'flex-col', gap: '4' },
    spacing: {
      padding: 'p-4',
    },
  })
  .canvas()
  .build()
